<template>
    <div class="payment-confirmation-popover">
        <div class="payment-state-section">
            <div class="circleloader" :class="{ fail: paymentFailed, success: paymentSuccess }">
                <div v-if="paymentFailed" class="failmark"><i class="fal fa-times"></i></div>
                <div v-if="paymentSuccess" class="checkmark draw"></div>
            </div>

            <div class="payment-state-section">
                <div class="checking">
                    <span v-if="paymentInProgress">{{ trans.done.trxCheckingTitle }}</span>
                    <span v-if="paymentSuccess">{{ trans.done.trxSuccessTitle }}</span>
                    <span v-if="paymentFailed">{{ trans.done.trxDeclinedTitle }}</span>
                </div>
                <div class="holdon">
                    <span v-if="paymentInProgress">{{ trans.done.trxCheckingText }}</span>
                    <span v-if="paymentFailed">{{ paymentFailedDescription }}</span>
                </div>
            </div>
        </div>

        <VButton
            v-if="
                isCheckWorldlineLastFailedTransactionAvailable() &&
                paymentProvider === PaymentProvider.Worldline &&
                paymentStatus != null &&
                paymentStatus === PaymentStatus.Failed
            "
            class="primary rounded clickable check-last-failed-transaction-btn"
            @click="checkWorldlineLastFailedTransaction"
            >{{ trans.done.validateStatusOfLastPayment }}</VButton
        >

        <VButton
            v-if="paymentStatus != null && paymentStatus !== PaymentStatus.InProgress"
            class="primary rounded clickable back-btn"
            @click="onClickBackButton"
            >{{ backButtonText }}</VButton
        >
    </div>
</template>

<script setup lang="ts">
    import { trans } from "@/app/localization/Trans";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import type { IOrderPaymentConfirmation } from "@/data/model/Customer";
    import { PaymentFor, PaymentProvider } from "@/data/model/Enums";
    import { Format } from "@/data/util/Format";
    import { logError } from "@/shared/logging/errors";
    import {
        checkWorldlineLastFailedTransaction,
        isCheckWorldlineLastFailedTransactionAvailable,
    } from "@/shared/utils/androidNativeUtils";
    import { computed, onBeforeUnmount, onMounted, ref } from "vue";

    import VButton from "@/components/elements/VButton.vue";

    enum PaymentStatus {
        InProgress,
        Success,
        Failed,
    }

    const props = defineProps<{
        transactionId: string | undefined;
        externalPaymentId: string | undefined;
        transactionFor: PaymentFor;
    }>();

    const emit = defineEmits(["closed"]);

    const paymentStatus = ref<PaymentStatus | undefined>(PaymentStatus.Success);
    const paymentProvider = ref<PaymentProvider | undefined>(undefined);
    const cancelLongpoll = ref<boolean>(false);
    const paymentFailedReason = ref<string | undefined>();

    const paymentInProgress = computed<boolean>(() => paymentStatus.value === PaymentStatus.InProgress);
    const paymentSuccess = computed<boolean>(() => paymentStatus.value === PaymentStatus.Success);
    const paymentFailed = computed<boolean>(() => paymentStatus.value === PaymentStatus.Failed);
    const paymentFailedDescription = computed<string>(() =>
        paymentFailedReason.value == null || paymentFailedReason.value.length === 0
            ? trans.done.trxDeclinedText
            : paymentFailedReason.value
    );

    const backButtonText = computed(() => {
        switch (paymentStatus.value) {
            case PaymentStatus.InProgress:
                return "";
            case PaymentStatus.Success:
                return trans.general.continue;
            case PaymentStatus.Failed:
                switch (props.transactionFor) {
                    case PaymentFor.Bill:
                        return trans.done.backToPayment;
                    case PaymentFor.Order:
                        return trans.done.backToOrder;
                }
        }

        return "";
    });

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    onMounted(async () => {
        cancelLongpoll.value = false;
        paymentStatus.value = undefined;
        await setValidatingTransaction();
    });

    onBeforeUnmount(() => {
        cancelLongpoll.value = true;
    });

    function onClickBackButton() {
        emit("closed");
    }

    async function setValidatingTransaction() {
        paymentStatus.value = PaymentStatus.InProgress;

        try {
            let result: IOrderPaymentConfirmation;
            if (props.transactionId != null) {
                result = await longpollTransaction(props.transactionId);
            } else if (props.externalPaymentId != null) {
                result = await longpollTransactionByExternalPaymentId(props.externalPaymentId!);
            } else {
                throw new Error("Transaction id and external payment id are missing");
            }

            if (result.isConfirmed === false) {
                paymentStatus.value = PaymentStatus.Failed;

                paymentFailedReason.value = `${result.error !== undefined ? Format.endWithFullstop(result.error) + " " : ""}${trans.done.trxDeclinedText}`;
                // show button
            } else {
                paymentStatus.value = PaymentStatus.Success;
                setTimeout(() => onClickBackButton(), 5000);
            }
        } catch (ex) {
            logError(ex);
            paymentStatus.value = PaymentStatus.Failed;
            paymentFailedReason.value = trans.done.trxFailedCheckingText;
        }
    }

    function longpollTransaction(transactionId: string): Promise<IOrderPaymentConfirmation> {
        return new Promise((resolve, reject) => {
            let retries = 0;

            const checkRecursive = async (transactionId: string) => {
                if (cancelLongpoll.value) return;

                try {
                    const result = await customerDao.checkOrderPaymentConfirmation(transactionId);
                    paymentProvider.value = result.provider;

                    if (retries > 50) {
                        reject();
                        return;
                    }

                    if (result.isConfirmed === undefined) {
                        retries++;

                        const delay = retries <= 1 ? 500 : retries <= 5 ? 2500 : 6000;
                        window.setTimeout(() => checkRecursive(transactionId), delay);
                    } else {
                        resolve(result);
                    }
                } catch (ex) {
                    logError(ex);
                    reject(ex);
                }
            };

            checkRecursive(transactionId);
        });
    }

    function longpollTransactionByExternalPaymentId(externalPaymentId: string): Promise<IOrderPaymentConfirmation> {
        return new Promise((resolve, reject) => {
            let retries = 0;

            const checkRecursive = async (externalPaymentId: string) => {
                if (cancelLongpoll.value) return;

                try {
                    const result =
                        await customerDao.checkOrderPaymentConfirmationByExternalPaymentId(externalPaymentId);
                    paymentProvider.value = result.provider;

                    if (retries > 50) {
                        reject();
                        return;
                    }

                    if (result.isConfirmed === undefined) {
                        retries++;

                        const delay = retries <= 1 ? 500 : retries <= 5 ? 2500 : 6000;
                        window.setTimeout(() => checkRecursive(externalPaymentId), delay);
                    } else {
                        resolve(result);
                    }
                } catch (ex) {
                    logError(ex);
                    reject(ex);
                }
            };

            checkRecursive(externalPaymentId);
        });
    }
</script>

<style scoped lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";

    .payment-confirmation-popover {
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        top: 0;
        background: white;
        padding-top: 62px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;

        .payment-state-section {
            display: flex;
            justify-content: center;
            flex-direction: column;

            > .circleloader {
                border: 2px solid rgba(0, 0, 0, 0.2);
                border-left-color: #8fb52c;
                animation: loader-spin 1.2s infinite linear;
                transition: border 500ms ease-out;
                position: relative;
                display: inline-block;
                vertical-align: top;
                border-radius: 50%;
                width: 150px;
                height: 150px;
                margin: auto;

                > .checkmark {
                    &.draw:after {
                        animation-duration: 800ms;
                        animation-timing-function: ease;
                        animation-name: checkmark;
                        transform: scaleX(-1) rotate(135deg);
                    }

                    &:after {
                        opacity: 1;
                        height: 75px;
                        width: 50px;
                        transform-origin: left top;
                        border-right: 2px solid #8fb52c;
                        border-top: 2px solid #8fb52c;
                        content: "";
                        left: 30px;
                        top: 68px;
                        position: absolute;
                    }
                }

                > .failmark {
                    text-align: center;
                    margin: auto;

                    > .fal {
                        font-size: 70px;
                        color: colors.$red;
                        line-height: 150px;
                        opacity: 0;
                        transform: scale(0);
                        transition: 0.3s ease-out;
                    }
                }

                &.success {
                    -webkit-animation: none;
                    animation: none;
                    border-color: #8fb52c;

                    > .checkmark {
                        display: block;
                    }
                }

                &.fail {
                    -webkit-animation: none;
                    animation: none;
                    border-color: colors.$red;

                    > .failmark {
                        display: block;

                        > .fal {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                }

                @keyframes loader-spin {
                    0% {
                        transform: rotate(0deg);
                    }

                    100% {
                        transform: rotate(360deg);
                    }
                }

                @keyframes checkmark {
                    0% {
                        height: 0;
                        width: 0;
                        opacity: 1;
                    }

                    20% {
                        height: 0;
                        width: 50px;
                        opacity: 1;
                    }

                    40% {
                        height: 75px;
                        width: 50px;
                        opacity: 1;
                    }

                    100% {
                        height: 75px;
                        width: 50px;
                        opacity: 1;
                    }
                }
            }

            > .payment-state-section {
                text-align: center;
                padding: 16px 0 0 0;

                > .checking {
                    font-weight: 400;
                    font-size: 16px;
                    letter-spacing: 0.3px;
                }

                > .holdon {
                    font-size: 14px;
                }
            }
        }

        .check-last-failed-transaction-btn {
            position: absolute;
            bottom: 68px;
            font-size: 17px;
            font-weight: 500;
            height: 2.75em;
            line-height: 2.75em;
            padding: 0 2em;
            border-radius: 1.5em !important;
            width: calc(100% - 30px);
        }

        .back-btn {
            position: absolute;
            bottom: 15px;
            font-size: 17px;
            font-weight: 500;
            height: 2.75em;
            line-height: 2.75em;
            padding: 0 2em;
            border-radius: 1.5em !important;
            width: calc(100% - 30px);
        }
    }
</style>